// Cmd/Ctrl+K palette entry point.
//
// This file is deliberately thin: it owns the trigger, the open state and
// the single global key listener. The dialog body — which drags in `cmdk`
// and every query it searches over — lives in a lazy chunk that nothing
// downloads until the operator actually reaches for search. Hovering or
// focusing the trigger warms that chunk so the first open feels instant.

import { Button } from "@veil/ui/components/button";
import { Kbd } from "@veil/ui/components/kbd";
import Search from "lucide-react/dist/esm/icons/search";
import { lazy, Suspense, useEffect, useState } from "react";

const PaletteDialog = lazy(() => import("./command-palette-dialog"));

function preloadPalette(): void {
	void import("./command-palette-dialog");
}

// Resolved once at module scope: the platform doesn't change mid-session,
// and a per-render UA sniff would be pure waste.
const IS_APPLE =
	typeof navigator !== "undefined" &&
	/Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
export const SHORTCUT_LABEL = IS_APPLE ? "⌘K" : "Ctrl K";

// The trigger lives in the header, but a page can need it too: the not-found
// route offers search as one of its two exits. Registering the opener here
// keeps the state in the component that owns it, and does nothing at all when
// no palette is mounted (the login and setup screens render without the shell).
const openers = new Set<() => void>();

export function openCommandPalette(): void {
	for (const open of openers) open();
}

export function CommandPalette() {
	const [open, setOpen] = useState(false);
	// Latches on first open so the dialog (and its chunk) stay mounted for
	// subsequent opens, but never mount for operators who don't use search.
	const [everOpened, setEverOpened] = useState(false);

	useEffect(() => {
		// Empty deps plus the functional setState form means exactly one
		// listener for the lifetime of the shell — no re-subscribe churn.
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "k" && event.key !== "K") return;
			if (!event.metaKey && !event.ctrlKey) return;
			event.preventDefault();
			setEverOpened(true);
			setOpen((prev) => !prev);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	useEffect(() => {
		const open = () => {
			setEverOpened(true);
			setOpen(true);
		};
		openers.add(open);
		return () => {
			openers.delete(open);
		};
	}, []);

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				aria-label={`Search and commands (${SHORTCUT_LABEL})`}
				onMouseEnter={preloadPalette}
				onFocus={preloadPalette}
				onClick={() => {
					setEverOpened(true);
					setOpen(true);
				}}
				className="h-7 gap-2 text-muted-foreground"
			>
				<Search className="size-3.5" />
				<span className="hidden sm:inline">Search</span>
				<Kbd className="hidden sm:inline-flex">{SHORTCUT_LABEL}</Kbd>
			</Button>

			{everOpened ? (
				<Suspense fallback={null}>
					<PaletteDialog open={open} onOpenChange={setOpen} />
				</Suspense>
			) : null}
		</>
	);
}
